import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MergeUnresolvedError } from "~/api/errors";
import { useCommitMerge, useMergeSession, useOpenMerge, useResolveMergePart } from "~/api/queries";
import type { MergeDecision, Revision } from "~/api/types";
import { MergeAuditDialog } from "~/features/merge/MergeAuditDialog";
import { MergePartRow } from "~/features/merge/MergePartRow";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatDateTime } from "~/foundation/utils/time";

interface Resolution {
  decision: MergeDecision;
}

/**
 * W9 §A.2.2 — open · preview · resolve · commit · audit.
 *
 * Slate lets the person CHOOSE the sides from the revision list (§E-6a) — kms silently took
 * the two most recent revisions off a `limit:5` page with no author awareness. Two failure
 * paths that are unreachable/invisible in kms are real here (§E-7): a 409 commit reads
 * `unresolved` off the body (the only place it is delivered), and closing without committing
 * warns once that the session stays `Open` server-side — there is no abandon endpoint yet
 * (backend follow-up B8).
 */
export function MergePanel({
  documentId,
  revisions,
  sessionId,
  onSessionOpened,
  onClose,
}: {
  documentId: string;
  revisions: Revision[];
  sessionId: string | null;
  onSessionOpened: (sessionId: string) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({});
  const [auditOpen, setAuditOpen] = useState(false);

  const openMerge = useOpenMerge();
  const session = useMergeSession(sessionId);
  const resolvePart = useResolveMergePart();
  const commit = useCommitMerge(documentId);

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const startMerge = () => {
    openMerge.mutate(
      { documentId, contributingRevisionIds: [...selected] },
      { onSuccess: (created) => onSessionOpened(created.id) },
    );
  };

  const resolve = (
    partId: string,
    decision: MergeDecision,
    chosenFrom?: string,
    combinedContent?: string,
  ) => {
    if (!sessionId) return;
    resolvePart.mutate(
      { sessionId, partId, decision, chosenFrom, combinedContent },
      { onSuccess: () => setResolutions((current) => ({ ...current, [partId]: { decision } })) },
    );
  };

  const doCommit = () => {
    if (!sessionId) return;
    commit.mutate(sessionId, {
      onSuccess: () => {
        // E-7: the server cannot promise the live collab room got it.
        toast.success(t("Merged. If the document is open elsewhere, reload it."));
        onClose();
      },
      onError: (error) => {
        if (!(error instanceof MergeUnresolvedError)) {
          toast.error(error.message);
        }
      },
    });
  };

  const closeWithoutCommitting = () => {
    if (sessionId) {
      // M9: no abandon endpoint exists yet (B8) — the session leaks as Open server-side.
      toast.warning(t("This merge stays open. You (or anyone else) can resume it later."));
    }
    onClose();
  };

  const commitError = commit.error instanceof MergeUnresolvedError ? commit.error : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && closeWithoutCommitting()}
      title={t("Merge revisions")}
      closeLabel={t("Close")}
      size="xl"
      footer={
        sessionId ? (
          <>
            <Button variant="ghost" onClick={() => setAuditOpen(true)}>
              {t("Audit")}
            </Button>
            <Button variant="ghost" onClick={closeWithoutCommitting}>
              {t("Cancel")}
            </Button>
            <Button variant="primary" disabled={commit.isPending} onClick={doCommit}>
              {t("Commit merge")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={selected.size < 2 || openMerge.isPending}
              onClick={startMerge}
            >
              {t("Start merge")}
            </Button>
          </>
        )
      }
    >
      {!sessionId ? (
        <div className="flex flex-col gap-2">
          <Text variant="small">{t("Pick at least two revisions to merge.")}</Text>
          <ul className="flex max-h-[360px] flex-col gap-1 overflow-y-auto">
            {revisions.map((revision) => (
              <li key={revision.id}>
                <label className="flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-hover-bg">
                  <input
                    type="checkbox"
                    checked={selected.has(revision.id)}
                    onChange={() => toggle(revision.id)}
                  />
                  <Avatar
                    name={revision.createdBy.name}
                    src={revision.createdBy.avatarUrl}
                    size={24}
                  />
                  <span className="min-w-0 flex-1 truncate text-body text-text">
                    {revision.createdBy.name}
                  </span>
                  <span className="shrink-0 text-small text-text-tertiary">
                    {formatDateTime(revision.createdAt, i18n.language)}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : session.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner label={t("Loading")} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {commitError ? (
            <Text variant="small" className="text-danger">
              {t("{{count}} parts still need a decision.", { count: commitError.unresolved })}
            </Text>
          ) : null}
          {(session.data ?? []).length > 0 &&
          (session.data ?? []).every((part) => part.partId.startsWith("seq:")) ? (
            // §E-14 — same degraded-identity signal as `RevisionParts`, surfaced here too:
            // this IS the review a person is about to trust a decision on.
            <Text variant="small">
              {t(
                "This document has no stable part identity yet — merging will compare by position",
              )}
            </Text>
          ) : null}
          <ul className="flex max-h-[420px] flex-col gap-2 overflow-y-auto">
            {(session.data ?? []).map((part) => (
              <MergePartRow
                key={part.partId}
                part={part}
                resolved={resolutions[part.partId]}
                onResolve={(decision, chosenFrom, combinedContent) =>
                  resolve(part.partId, decision, chosenFrom, combinedContent)
                }
              />
            ))}
          </ul>
        </div>
      )}
      {auditOpen && sessionId ? (
        <MergeAuditDialog sessionId={sessionId} onClose={() => setAuditOpen(false)} />
      ) : null}
    </Dialog>
  );
}
